import type { FC } from 'react';
import { useMemo } from 'react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useChartTokens, useTooltipStyle } from './ChartKit';
import type { SerieDiariaAnuncio } from '../services/TrafegoPagoService';

/**
 * Gasto × leads, dia a dia. docs/designsystem/propostas/anuncios.html §6.2.
 *
 * **Duas séries de verdade, então primary + secondary** — é o caso que a regra
 * do ChartKit prevê: gasto e leads são grandezas diferentes, não passos da mesma.
 * Por isso também são dois eixos: dinheiro à esquerda, contagem à direita.
 *
 * **O gasto é barra e o lead é linha**, de propósito. Barra lê como "quanto
 * saiu naquele dia", que é um fato fechado; linha lê como percurso. Duas linhas
 * sobrepostas em escalas diferentes convidam a comparar altura, que aqui não
 * quer dizer nada.
 *
 * A série chega COMPLETA da API — dia sem movimento vem com zero, não ausente.
 * Sem isso a linha pularia o fim de semana e desenharia uma queda que não houve.
 */
const GraficoAnunciosPorDia: FC<{ serie: SerieDiariaAnuncio[]; moeda: string }> = ({ serie, moeda }) => {
    const t = useChartTokens();
    const tooltip = useTooltipStyle();

    const dados = useMemo(() => serie.map(d => ({
        ...d,
        /* Rótulo curto: com 90 dias no eixo, "24/09/2026" vira uma mancha. O dia
           inteiro continua no tooltip. */
        rotulo: d.dia.slice(8, 10) + '/' + d.dia.slice(5, 7),
    })), [serie]);

    const dinheiro = (v: number) => new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: moeda || 'BRL', maximumFractionDigits: 0,
    }).format(v);

    return (
        <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={dados} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={t.grid} vertical={false} />
                <XAxis
                    dataKey="rotulo"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    /* Com período longo o eixo se auto-rareia; sem isto o recharts
                       desenha um rótulo por dia e eles se sobrepõem. */
                    interval="preserveStartEnd"
                    minTickGap={24}
                />
                <YAxis
                    yAxisId="dinheiro"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    tickFormatter={dinheiro}
                    width={64}
                />
                <YAxis
                    yAxisId="contagem"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    /* Contagem é inteiro: sem isto o eixo mostra "0,5 lead". */
                    allowDecimals={false}
                    width={34}
                />
                <Tooltip
                    contentStyle={tooltip.contentStyle}
                    labelStyle={tooltip.labelStyle}
                    itemStyle={tooltip.itemStyle}
                    formatter={(valor: number, nome: string) =>
                        [nome === 'Investido' ? dinheiro(valor) : valor, nome]}
                />
                <Bar
                    yAxisId="dinheiro"
                    dataKey="gasto"
                    name="Investido"
                    fill={t.primary}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={22}
                />
                {/* `linear`, NÃO `monotone`: contagem de lead é inteira e pequena,
                    e a curva suave infla entre os pontos — desenha morros de "meio
                    lead" em dias que tiveram zero. É a mesma régua do travessão na
                    tabela: o desenho não pode afirmar o que não se mediu.
                    O ponto aparece porque, com a reta, um dia isolado com 1 lead
                    entre dois zeros vira um pico de 1px sem ele. */}
                <Line
                    yAxisId="contagem"
                    type="linear"
                    dataKey="leads"
                    name="Leads"
                    stroke={t.secondary}
                    strokeWidth={2}
                    dot={{ r: 2, fill: t.secondary, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default GraficoAnunciosPorDia;
